import {ErrorMap, ErrorMapItem} from "@/types.ts";
import {ArticleErrorCode} from "@/enums.ts";
import {
  ArticleFetchError,
  ArticleInvalidRequestError,
  ArticleNotFoundError,
  BranderMissingReplacementParamError,
  IncorrectArticleIdSeparatorError,
  InvalidArticleIdError,
  MissingArticleIdParameterError,
  TooManyArticleIdsError
} from "@/errors.ts";
import {LogCode, LogLevel} from "@/logger";

type ResolveErrorDetailsResult = {
  errorToThrow: Error;
  code: string;
  logMessage: string;
  logCode: LogCode | string;
};

type HandleErrorResult = {
  errorToThrow: Error;
  userMessage: string;
  logMessage: string;
  logCode: LogCode | string;
};

type MappedErrorDetails = {
  code: ArticleErrorCode;
  data?: { count?: string };
};

type ErrorWithCode = Error & { code?: string };

const viewArticleErrorMap: ErrorMap = {
  [ArticleErrorCode.VIEW_ARTICLE_INVALID_ID]: {
    errorConstructor: InvalidArticleIdError,
    logCode: LogCode.VIEW_ARTICLE_INVALID_ID,
    logMessage: 'The provided article ID is invalid.',
    logLevel: LogLevel.ERROR,
  },
  [ArticleErrorCode.ARTICLE_NOT_FOUND]: {
    errorConstructor: ArticleNotFoundError,
    logCode: LogCode.ARTICLE_NOT_FOUND,
    logMessage: 'The requested article could not be found.',
    logLevel: LogLevel.ERROR,
  },
  [ArticleErrorCode.VIEW_ARTICLE_INCORRECT_ID_SEPARATOR]: {
    errorConstructor: IncorrectArticleIdSeparatorError,
    logCode: LogCode.VIEW_ARTICLE_INCORRECT_ID_SEPARATOR,
    logMessage: 'The article ID parameter contains an incorrect separator.',
    logLevel: LogLevel.ERROR,
  },
  [ArticleErrorCode.VIEW_ARTICLE_TOO_MANY_IDS]: {
    errorConstructor: TooManyArticleIdsError,
    logCode: LogCode.VIEW_ARTICLE_TOO_MANY_IDS,
    logMessage: 'Too many article IDs were provided in the request.',
    logLevel: LogLevel.ERROR,
  },
  [ArticleErrorCode.VIEW_ARTICLE_MISSING_ID_PARAM]: {
    errorConstructor: MissingArticleIdParameterError,
    logCode: LogCode.VIEW_ARTICLE_MISSING_ID_PARAM,
    logMessage: 'The required article ID parameter is missing.',
    logLevel: LogLevel.ERROR,
  },
  [ArticleErrorCode.BRANDER_MISSING_REPLACEMENT_PARAM]: {
    errorConstructor: BranderMissingReplacementParamError,
    logCode: LogCode.BRANDER_MISSING_REPLACEMENT_PARAM,
    logMessage: 'Brander is missing a replacement template parameter.',
    logLevel: LogLevel.ERROR,
  },
  [ArticleErrorCode.INVALID_REQUEST]: {
    errorConstructor: ArticleInvalidRequestError,
    logCode: LogCode.FAILED_TO_FETCH_ARTICLE,
    logMessage: 'Invalid request occurred when fetching Article.',
    logLevel: LogLevel.ERROR,
  },
}

function getDetailsForInvalidRequest(errorMessage: string): MappedErrorDetails {
  // Can be prefixed with either `id: ` or `ids: `
  if (errorMessage.includes('Input should be a valid integer, unable to parse string as an integer')) {
    return { code: ArticleErrorCode.VIEW_ARTICLE_INVALID_ID };
  }
  if (errorMessage === "ids: Unrecognised value for the 'ids' parameter - please check the separator") {
    return { code: ArticleErrorCode.VIEW_ARTICLE_INCORRECT_ID_SEPARATOR };
  }
  const tooManyIdsMatch = RegExp(/ids: List should have at most 100 items after validation, not (\d+)/).exec(errorMessage);
  if (tooManyIdsMatch?.[1]) {
    return { code: ArticleErrorCode.VIEW_ARTICLE_TOO_MANY_IDS, data: { count: tooManyIdsMatch[1] } };
  }
  return { code: ArticleErrorCode.INVALID_REQUEST };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getArticleErrorCode(e: ErrorWithCode | any): MappedErrorDetails {
  // If the API returns a generic 'InvalidRequest', we need to parse the message.
  if (e.code === 'InvalidRequest' && e.message) {
    return getDetailsForInvalidRequest(e.message);
  }
  // Otherwise, use the code from the error if it exists, or fall back to default.
  return { code: e.code ?? ArticleErrorCode.VIEW_ARTICLE_UNKNOWN_ERROR };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function createErrorToThrow(mappedError: ErrorMapItem, details: MappedErrorDetails, originalError: ErrorWithCode | any): Error {
  const errorInstance = new mappedError.errorConstructor();
  // If the error is an InvalidRequest, it's an error from the API that hasn't been caught by our map
  // so we display the message as it came back from the API.
  let uiErrorMessage = details.code === ArticleErrorCode.INVALID_REQUEST ? originalError.message : errorInstance.message;

  // Append extra context for specific errors
  if (details.code === ArticleErrorCode.VIEW_ARTICLE_TOO_MANY_IDS && details.data?.count) {
    uiErrorMessage = `${uiErrorMessage} Received ${details.data.count}.`;
  }

  errorInstance.message = uiErrorMessage;
  return errorInstance;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function resolveErrorDetails(originalError: ErrorWithCode | any, mappedError?: ErrorMapItem, errorDetails?: MappedErrorDetails): ResolveErrorDetailsResult {
  if (mappedError && errorDetails) {
    return {
      errorToThrow: createErrorToThrow(mappedError, errorDetails, originalError),
      logMessage: `${mappedError.logMessage}: ${originalError.message}`,
      logCode: mappedError.logCode,
      code: errorDetails.code,
    }
  }

  if (originalError instanceof Error) {
    const code = (originalError as ErrorWithCode).code ?? LogCode.VIEW_ARTICLE_UNKNOWN_ERROR;
    return {
      errorToThrow: originalError,
      logMessage: originalError.message,
      code: code,
      logCode: code,
    };
  }

  const code = originalError.code ?? LogCode.VIEW_ARTICLE_UNKNOWN_ERROR;
  const errorToThrow = originalError.message
    ? new ArticleFetchError(originalError.message)
    : new ArticleFetchError();
  return {
    errorToThrow,
    logMessage: errorToThrow.message,
    logCode: code,
    code: code,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function handleError(originalError: ErrorWithCode | any, correlationId?: string): HandleErrorResult {
  const details = getArticleErrorCode(originalError);
  const mappedError = viewArticleErrorMap[details.code];

  const {
    errorToThrow,
    logMessage,
    logCode,
    code,
  } = resolveErrorDetails(originalError, mappedError, details);

  const userMessage = correlationId
    ? `${errorToThrow.message}\n\nError code: ${code}\nReference ID: ${correlationId}`
    : `${errorToThrow.message}\n\nError code: ${code}`;

  return {
    errorToThrow,
    userMessage,
    logMessage,
    logCode,
  };
}

export {
  viewArticleErrorMap,
  handleError,
  resolveErrorDetails,
  createErrorToThrow,
  getArticleErrorCode,
  getDetailsForInvalidRequest,
};
